import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import ApplicationStore from 'state/ApplicationStore';
import { initialDeclarationStepFormValues } from 'models/forms/applicationForm';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import {
  StyledParagraph,
  StyledSectionTitle,
  StyledStepTitle,
} from 'pages/ApplyOnlinePage/ApplyOnlineWizardPage/WizardSteps/styles';
import { useDeclarationStepFormSchema } from './schemaValidations';

const DeclarationStep = () => {
  const { schema } = useDeclarationStepFormSchema();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialDeclarationStepFormValues,
    resolver: yupResolver(schema),
  });

  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    const handler = () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    };
    document.addEventListener('submitFormEvent', handler);
    return () => {
      document.removeEventListener('submitFormEvent', handler);
    };
  }, []);

  const onSubmitForm = (data: any) => {
    ApplicationStore.setStepData('declarationData', data);
  };

  return (
    <Box>
      <StyledStepTitle variant="h4">Declaration</StyledStepTitle>

      <form onSubmit={handleSubmit(onSubmitForm)} ref={formRef}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <FormCheckbox
              name="declaration"
              label="I hereby apply for a Shotgun Certificate"
              control={control}
              errors={errors}
            />
          </Grid>
        </Grid>
      </form>

      <StyledParagraph variant="body2">
        The information I have provided on this form is true and I understand that it is an
        offence under section 28A(7) of the Firearms Act 1968 to knowingly or recklessly make a
        false statement for the purpose of procuring the grant or renewal of a certificate, the
        maximum penalty for which is six months’ imprisonment and/or a fine. I understand that
        I will be subject to a check of police records and that my details will be held
        electronically.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        I understand that if I do not provide the required information my application cannot be
        processed and will be refused.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        I understand that I am expected to inform the police if I am diagnosed with, or treated
        for, a medical condition listed in note 5 while the certificate remains valid.
      </StyledParagraph>

      <StyledSectionTitle variant="h5">Data Protection</StyledSectionTitle>
      <StyledParagraph variant="body2">
        I understand that all information submitted will be handled in accordance with the Data
        Protection Act 2018 and the Freedom of Information Act 2000 and connected legislation.
        I understand that information contained within my application form or obtained in the
        course of deciding the application may be shared with: my doctor, other government
        departments, regulatory bodies or enforcement agencies in the course of deciding the
        application or in pursuance of maintaining public safety or the peace.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Note: Any information shared will be shared in accordance with data sharing protocols.
        The police do not share your personal details with other applicants or members of the
        public and treat information in connection with the application in confidence, but
        individuals should be aware that the police may disclose some information in accordance
        with the legislation referred to above.
      </StyledParagraph>
      <StyledParagraph variant="body2">
        Your personal data will be processed by the police force to which you apply in line
        with Part 3 of the Data Protection Act 2018 and as set out in the force’s Privacy
        Information Notice.
      </StyledParagraph>
    </Box>
  );
};

export default DeclarationStep;
